import { useCallback } from 'react';
import { Editor, Range as SlateRange, Text as SlateText } from 'slate';
import { ReactEditor } from 'slate-react';

/**
 * Paints the selection the editor was left with, which the browser stops painting the moment the
 * editor is blurred.
 *
 * `keepWhileFocused` covers the case of something that takes the focus without blurring the editor
 * as far as slate is concerned — a popover rendered inside it, whose blur the editor swallows to
 * stay in the same editing session. Slate goes on believing it holds the focus, so the selection
 * has to be painted on the caller's word rather than on that flag.
 */
export const useDecorateUnfocusedSelection = (editor, { keepWhileFocused = false } = {}) => {
  return useCallback(
    ([node, path]) => {
      if (!SlateText.isText(node)) {
        return [];
      }

      const ranges = [];

      if (keepWhileFocused || !ReactEditor.isFocused(editor)) {
        // Slate keeps its own selection across a blur, and it is the one an improve acts on; the
        // stored previous selection is preferred only because a plugin may have moved it since.
        const selectionRange = editor.getPreviousSelection?.() ?? editor.selection;
        if (selectionRange) {
          const nodeRange = Editor.range(editor, path);
          const intersection = SlateRange.intersection(selectionRange, nodeRange);
          if (intersection) {
            ranges.push({
              anchor: intersection.anchor,
              focus: intersection.focus,
              selection: true,
            });
          }
        }
      }

      return ranges;
    },
    [editor, keepWhileFocused]
  );
};
